import {
  AUTH_STORAGE_KEY,
  UserRole,
  type AuthSession,
  type AuthUser,
} from '../constants/auth'

function isUserRole(value: unknown): value is UserRole {
  return (
    value === UserRole.Admin ||
    value === UserRole.Customer ||
    value === UserRole.Vendor
  )
}

function parseUser(raw: unknown): AuthUser | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  if (typeof record.id !== 'string' || !isUserRole(record.role)) return null
  return {
    id: record.id,
    role: record.role,
    vendorId: typeof record.vendorId === 'string' ? record.vendorId : null,
    name: typeof record.name === 'string' ? record.name : null,
    phone: typeof record.phone === 'string' ? record.phone : null,
    email: typeof record.email === 'string' ? record.email : null,
    phoneVerified: Boolean(record.phoneVerified),
    emailVerified: Boolean(record.emailVerified),
    isActive: record.isActive !== false,
  }
}

export function parseAuthSession(raw: string | null): AuthSession | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const user = parseUser(parsed.user)
    if (
      !user ||
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.refreshToken !== 'string'
    ) {
      return null
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      user,
    }
  } catch {
    return null
  }
}

export function readAuthSession(): AuthSession | null {
  if (typeof window === 'undefined') return null
  return parseAuthSession(window.localStorage.getItem(AUTH_STORAGE_KEY))
}

type AuthSessionListener = (session: AuthSession | null) => void

const authSessionListeners = new Set<AuthSessionListener>()

function emitAuthSession(session: AuthSession | null): void {
  for (const listener of authSessionListeners) {
    listener(session)
  }
}

export function subscribeAuthSession(listener: AuthSessionListener): () => void {
  authSessionListeners.add(listener)
  return () => {
    authSessionListeners.delete(listener)
  }
}

export function writeAuthSession(session: AuthSession): void {
  const serialized = JSON.stringify(session)
  if (window.localStorage.getItem(AUTH_STORAGE_KEY) === serialized) return
  window.localStorage.setItem(AUTH_STORAGE_KEY, serialized)
  emitAuthSession(session)
}

export function clearAuthSession(): void {
  window.localStorage.removeItem(AUTH_STORAGE_KEY)
  emitAuthSession(null)
}

export function getAccessToken(): string | null {
  return readAuthSession()?.accessToken ?? null
}

export function getRefreshToken(): string | null {
  return readAuthSession()?.refreshToken ?? null
}

export function isVendorSession(
  session: AuthSession | null,
): session is AuthSession {
  return Boolean(
    session &&
      session.user.role === UserRole.Vendor &&
      session.user.isActive &&
      session.user.vendorId,
  )
}
